/**
 * ModelBuilder — standalone dental study-model builder.
 */
import React, { useState, useCallback, useEffect } from 'react';
import {
    Box, Typography, Slider, Button, TextField, CircularProgress,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import * as THREE from 'three';
import { STLExporter } from 'three/examples/jsm/exporters/STLExporter';
import { modelMaker, preloadFont } from './meshOps';

export const ModelBuilder = ({
    geometry,
    onResult,
    onExport,
    processing: externalProcessing,
    onProcessingChange,
    resultGeometry,
    accentColor = '#ef5350',
}) => {
    const [baseHeight, setBaseHeight] = useState(2);
    const [border, setBorder] = useState(2);
    const [text, setText] = useState('');
    const [internalProcessing, setInternalProcessing] = useState(false);
    const thickness = 0.5;

    const processing = externalProcessing ?? internalProcessing;
    const setProcessing = onProcessingChange ?? setInternalProcessing;

    useEffect(() => { preloadFont(); }, []);

    const handleBuild = useCallback(async () => {
        if (!geometry || processing) return;
        setProcessing(true);
        try {
            await preloadFont();
            await new Promise(resolve => setTimeout(resolve, 0));
            const result = modelMaker(geometry, {
                baseHeight,
                thickness,
                border,
                text,
            });
            onResult(result);
        } finally {
            setProcessing(false);
        }
    }, [geometry, baseHeight, border, text, processing, onResult, setProcessing]);

    const handleExport = useCallback(() => {
        const geo = resultGeometry;
        if (!geo) return;

        const tmpMesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial());
        const exporter = new STLExporter();
        const stlString = exporter.parse(tmpMesh);
        const blob = new Blob([stlString], { type: 'application/octet-stream' });

        if (onExport) {
            onExport(blob, 'model.stl');
        } else {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'model.stl';
            a.click();
            URL.revokeObjectURL(url);
        }
    }, [resultGeometry, onExport]);

    return (
        <Box sx={{ px: 0.5, minWidth: 140 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6rem', fontWeight: 600, width: 52, flexShrink: 0 }}>
                    Base Height
                </Typography>
                <Slider
                    size="small"
                    value={baseHeight}
                    onChange={(_, v) => setBaseHeight(v)}
                    min={2} max={30} step={1}
                    sx={{ color: accentColor, '& .MuiSlider-thumb': { width: 12, height: 12 } }}
                />
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6rem', width: 32, textAlign: 'right', flexShrink: 0 }}>
                    {baseHeight}mm
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6rem', fontWeight: 600, width: 52, flexShrink: 0 }}>
                    Border
                </Typography>
                <Slider
                    size="small"
                    value={border}
                    onChange={(_, v) => setBorder(v)}
                    min={0.5} max={8} step={0.5}
                    sx={{ color: accentColor, '& .MuiSlider-thumb': { width: 12, height: 12 } }}
                />
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6rem', width: 32, textAlign: 'right', flexShrink: 0 }}>
                    {border.toFixed(1)}
                </Typography>
            </Box>
            <TextField
                size="small"
                placeholder="Name on border"
                value={text}
                onChange={e => setText(e.target.value)}
                variant="outlined"
                sx={{
                    mt: 0.5, width: '100%',
                    '& .MuiOutlinedInput-root': {
                        color: '#fff', fontSize: '0.7rem', height: 28,
                        '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                        '&:hover fieldset': { borderColor: `${accentColor}80` },
                        '&.Mui-focused fieldset': { borderColor: accentColor },
                    },
                    '& .MuiOutlinedInput-input': { py: '4px', px: 1 },
                }}
            />
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                <Button
                    size="small"
                    variant="contained"
                    disabled={!geometry || processing}
                    onClick={handleBuild}
                    startIcon={processing ? <CircularProgress size={12} sx={{ color: 'rgba(255,255,255,0.5)' }} /> : undefined}
                    sx={{
                        flex: 1, bgcolor: accentColor, fontSize: '0.65rem', py: '3px', textTransform: 'none',
                        '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' },
                        '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.2)' },
                    }}
                >
                    {processing ? 'Building...' : 'Build'}
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                    disabled={!resultGeometry}
                    onClick={handleExport}
                    sx={{
                        flex: 1, color: '#66bb6a', borderColor: 'rgba(102,187,106,0.4)', fontSize: '0.65rem', py: '3px', textTransform: 'none',
                        '&:hover': { borderColor: '#66bb6a', bgcolor: 'rgba(102,187,106,0.1)' },
                        '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)', borderColor: 'rgba(255,255,255,0.08)' },
                    }}
                >
                    Export
                </Button>
            </Box>
        </Box>
    );
};
